/**
 * v2 콘텐츠 로더 (서·혼·각인·무기·마스터 이벤트·마스터 장비·재료).
 *
 * <p>2026-04-23: 사용자가 채운 `docs/content/combat-v2-templates/*.json` 을 빌드 시
 * `public/content/` 로 복사해 두고, 런타임에 fetch 로 로드. 한 번 로드 후 메모리 캐시.
 *
 * <p>설계 원칙 (combat-redesign-260423.md §9 확장성):
 * <ul>
 *   <li>콘텐츠 스키마는 JSON 으로 유연 — 필드 추가·삭제 시 타입만 느슨하게</li>
 *   <li>id 고정, 표시 라벨은 변경 가능</li>
 *   <li>미로드 시 빈 객체 반환 — 호출부가 안전하게 처리</li>
 * </ul>
 */

// 2026-04-23 Docker build 수정: 이전에 `../../../docs/...` 로 docs/ 참조 → Docker
//   build context 밖이라 tsc 에러. docs/content/combat-v2-templates/ 를 빌드 시 본
//   파일 옆 v2-fallback/ 으로 복사해서 import. 런타임 fetch 가 실패해도 build-time
//   번들된 콘텐츠를 폴백으로 사용. 원본은 docs/ 에 계속 유지 (사용자 편집 지점).
import fallbackSeoSkills from "./v2-fallback/seo-skills.json";
import fallbackHonSkills from "./v2-fallback/hon-skills.json";
import fallbackWeaponFlavor from "./v2-fallback/weapon-flavor.json";
import fallbackMasteryEvents from "./v2-fallback/mastery-events.json";
import fallbackMasteryItems from "./v2-fallback/mastery-items.json";
import fallbackEngraveFlavor from "./v2-fallback/engrave-flavor.json";
import fallbackEngraveMaterials from "./v2-fallback/engrave-materials.json";

// ═════════════════════════════════════════════════════════════════════════════
// 타입 정의 (JSON 구조 반영)
// ═════════════════════════════════════════════════════════════════════════════

export interface SeoSkill {
    id: string;
    label: string;
    rarity: "COMMON" | "RARE" | "LEGENDARY";
    unlockLevel: number;
    usage: string;
    allowedWeapons: string[];
    cooldownRough: string;
    castRough: string;
    costRough: string;
    flavor: string;
    effectSketch: string;
}

export interface SeoGroup {
    label: string;
    tagline: string;
    skills: SeoSkill[];
}

/** 서/혼 구조 같음. */
export type HonSkill = SeoSkill;
export type HonGroup = SeoGroup;

export interface WeaponFlavor {
    label: string;
    shortBlurb: string;
    longDescription: string;
    trainingHint: string;
}

export interface MasteryEventBeat {
    type: "narration" | "dialogue";
    text: string;
    speaker?: string;
}

export interface MasteryEvent {
    seoOrHon: "seo" | "hon";
    label: string;
    title: string;
    subtitle: string;
    tone: string;
    beats: MasteryEventBeat[];
    reward: { itemId: string; title: string; narrativeFlag: string };
}

export interface MasteryItem {
    label: string;
    category: "WEAPON" | "ARMOR" | "ACCESSORY" | "CHARM";
    weaponProfileId: string | null;
    shortBlurb: string;
    lore: string;
    effectSketch: string;
    defaultEngrave: string | null;
    defaultEngraveGrade: "standard" | "refined" | "crystal" | null;
}

export interface EngraveFlavor {
    label: string;
    meaning: string;
    poeticDescription: string;
    forgeLine: string;
}

export interface EngraveMaterial {
    engraveId: string;
    grade: "standard" | "refined" | "crystal";
    label: string;
    shortDesc: string;
    acquisitionHint: string;
}

// ═════════════════════════════════════════════════════════════════════════════
// 로더
// ═════════════════════════════════════════════════════════════════════════════

interface V2Content {
    seoSkills: Record<string, SeoGroup>;
    honSkills: Record<string, HonGroup>;
    weaponFlavor: Record<string, WeaponFlavor>;
    masteryEvents: Record<string, MasteryEvent>;
    masteryItems: Record<string, MasteryItem>;
    engraveFlavor: Record<string, EngraveFlavor>;
    engraveMaterials: Record<string, EngraveMaterial>;
}

export interface V2ContentState {
    content: V2Content;
    loading: boolean;
}

const EMPTY_CONTENT: V2Content = {
    seoSkills: {},
    honSkills: {},
    weaponFlavor: {},
    masteryEvents: {},
    masteryItems: {},
    engraveFlavor: {},
    engraveMaterials: {},
};

let cache: V2Content | null = null;
let inflightPromise: Promise<V2Content> | null = null;

const BUNDLED_FALLBACK: V2Content = {
    seoSkills: stripComments<SeoGroup>(fallbackSeoSkills),
    honSkills: stripComments<HonGroup>(fallbackHonSkills),
    weaponFlavor: stripComments<WeaponFlavor>(fallbackWeaponFlavor),
    masteryEvents: stripComments<MasteryEvent>(fallbackMasteryEvents),
    masteryItems: stripComments<MasteryItem>(fallbackMasteryItems),
    engraveFlavor: stripComments<EngraveFlavor>(fallbackEngraveFlavor),
    engraveMaterials: stripComments<EngraveMaterial>(fallbackEngraveMaterials),
};

function resolveContentPath(path: string): string {
    const base = (import.meta.env.BASE_URL ?? "/").trim();
    const trimmedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${trimmedBase}${normalizedPath}` || normalizedPath;
}

async function fetchJson<T>(path: string): Promise<T | null> {
    const resolved = resolveContentPath(path);
    const cacheBust = encodeURIComponent(
        (import.meta.env.APP_BUILT_AT as string | undefined)
        ?? (import.meta.env.APP_VERSION as string | undefined)
        ?? "dev",
    );
    const requestUrl = resolved.includes("?") ? `${resolved}&v=${cacheBust}` : `${resolved}?v=${cacheBust}`;
    try {
        const r = await fetch(requestUrl, { cache: "no-store" });
        if (!r.ok) {
            console.warn(`[combatV2Content] ${requestUrl} → HTTP ${r.status}`);
            return null;
        }
        const ct = r.headers.get("content-type") ?? "";
        const body = await r.text();
        const leading = body.trimStart().slice(0, 64).toLowerCase();

        // 2026-04-23: SPA fallback 방어.
        // 일부 환경은 JSON 요청에도 text/plain 또는 text/html 로 index.html 을 돌려주므로,
        // content-type 뿐 아니라 본문 시작부까지 함께 검사한다.
        if (leading.startsWith("<!doctype") || leading.startsWith("<html") || leading.startsWith("<head") || leading.startsWith("<body")) {
            console.warn(`[combatV2Content] ${requestUrl} returned HTML fallback instead of JSON`);
            return null;
        }

        if (!ct.includes("json") && !ct.includes("text/plain") && !ct.includes("javascript")) {
            console.warn(`[combatV2Content] ${requestUrl} unexpected content-type: ${ct}`);
        }

        try {
            return JSON.parse(body) as T;
        } catch (err) {
            console.warn(`[combatV2Content] ${requestUrl} JSON parse failed`, err);
            return null;
        }
    } catch (err) {
        console.warn(`[combatV2Content] ${requestUrl} fetch failed`, err);
        return null;
    }
}

function isContentEmpty(content: V2Content): boolean {
    return Object.values(content).every((section) => Object.keys(section).length === 0);
}

/** 주석 필드 (_comment 등) 제거한 정제된 레코드 반환. */
function stripComments<T>(raw: unknown): Record<string, T> {
    if (typeof raw !== "object" || raw === null) return {};
    const out: Record<string, T> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (k.startsWith("_")) continue;
        out[k] = v as T;
    }
    return out;
}

function preferFetched<T>(sectionName: string, fetched: Record<string, T>, fallback: Record<string, T>): Record<string, T> {
    if (Object.keys(fetched).length > 0) return fetched;
    console.warn(`[combatV2Content] ${sectionName} fell back to bundled snapshot`);
    return fallback;
}

/** 콘텐츠 전체 로드 (캐시됨). 미로드 시 빈 객체 반환. */
export async function loadV2Content(): Promise<V2Content> {
    if (cache) return cache;
    if (inflightPromise) return inflightPromise;

    inflightPromise = (async () => {
        const [seo, hon, weapon, events, items, engraveF, engraveM] = await Promise.all([
            fetchJson<Record<string, unknown>>("/content/seo-skills.json"),
            fetchJson<Record<string, unknown>>("/content/hon-skills.json"),
            fetchJson<Record<string, unknown>>("/content/weapon-flavor.json"),
            fetchJson<Record<string, unknown>>("/content/mastery-events.json"),
            fetchJson<Record<string, unknown>>("/content/mastery-items.json"),
            fetchJson<Record<string, unknown>>("/content/engrave-flavor.json"),
            fetchJson<Record<string, unknown>>("/content/engrave-materials.json"),
        ]);

        const loaded: V2Content = {
            seoSkills: preferFetched("seo-skills", stripComments<SeoGroup>(seo ?? {}), BUNDLED_FALLBACK.seoSkills),
            honSkills: preferFetched("hon-skills", stripComments<HonGroup>(hon ?? {}), BUNDLED_FALLBACK.honSkills),
            weaponFlavor: preferFetched("weapon-flavor", stripComments<WeaponFlavor>(weapon ?? {}), BUNDLED_FALLBACK.weaponFlavor),
            masteryEvents: preferFetched("mastery-events", stripComments<MasteryEvent>(events ?? {}), BUNDLED_FALLBACK.masteryEvents),
            masteryItems: preferFetched("mastery-items", stripComments<MasteryItem>(items ?? {}), BUNDLED_FALLBACK.masteryItems),
            engraveFlavor: preferFetched("engrave-flavor", stripComments<EngraveFlavor>(engraveF ?? {}), BUNDLED_FALLBACK.engraveFlavor),
            engraveMaterials: preferFetched("engrave-materials", stripComments<EngraveMaterial>(engraveM ?? {}), BUNDLED_FALLBACK.engraveMaterials),
        };

        if (isContentEmpty(loaded)) {
            console.warn("[combatV2Content] all content sections came back empty; skipping cache to allow retry");
            return loaded;
        }

        cache = loaded;
        return loaded;
    })();

    try {
        return await inflightPromise;
    } finally {
        inflightPromise = null;
    }
}

/** React 훅 — Encyclopedia·Admin 등에서 사용. */
import { useEffect, useState } from "react";

export function useV2Content(): V2Content {
    const [content, setContent] = useState<V2Content>(cache ?? EMPTY_CONTENT);
    useEffect(() => {
        let cancelled = false;
        void loadV2Content().then(c => {
            if (!cancelled) setContent(c);
        });
        return () => { cancelled = true; };
    }, []);
    return content;
}

export function useV2ContentState(): V2ContentState {
    const [content, setContent] = useState<V2Content>(cache ?? EMPTY_CONTENT);
    const [loading, setLoading] = useState<boolean>(cache == null);
    useEffect(() => {
        let cancelled = false;
        setLoading(cache == null);
        void loadV2Content().then(c => {
            if (!cancelled) {
                setContent(c);
                setLoading(false);
            }
        }).catch(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);
    return { content, loading };
}

/** 특정 seo 그룹 찾기. 못 찾으면 null. */
export function findSeoGroup(content: V2Content, seoId: string): SeoGroup | null {
    return content.seoSkills[seoId] ?? null;
}

/** 특정 hon 그룹. */
export function findHonGroup(content: V2Content, honId: string): HonGroup | null {
    return content.honSkills[honId] ?? null;
}

/** 특정 무기 flavor. */
export function findWeaponFlavor(content: V2Content, weaponId: string): WeaponFlavor | null {
    return content.weaponFlavor[weaponId] ?? null;
}

/** 특정 각인 flavor. */
export function findEngraveFlavor(content: V2Content, engraveId: string): EngraveFlavor | null {
    return content.engraveFlavor[engraveId] ?? null;
}

/** 특정 각인 재료 전체 (3 등급). */
export function findEngraveMaterials(content: V2Content, engraveId: string): EngraveMaterial[] {
    return Object.values(content.engraveMaterials).filter(m => m.engraveId === engraveId);
}

/** 마스터 이벤트 by seoOrHon + label. */
export function findMasteryEvent(content: V2Content, masteryId: string): MasteryEvent | null {
    return content.masteryEvents[masteryId] ?? null;
}

/** 마스터 장비 by id. */
export function findMasteryItem(content: V2Content, itemId: string): MasteryItem | null {
    return content.masteryItems[itemId] ?? null;
}

/** 전체 콘텐츠를 비워서 다음 로드 시 재fetch. 콘텐츠 수정 후 핫리로드용. */
export function resetV2ContentCache(): void {
    cache = null;
    inflightPromise = null;
}
